import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { FiSearch, FiTrash2, FiRefreshCw, FiFilter, FiX, FiEye, FiCheckSquare, FiAlertTriangle, FiCheck, FiClock, FiBox, FiTruck, FiPackage, FiChevronRight, FiDollarSign, FiList, FiXCircle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const ADMIN_STATUS_OPTIONS = ['pending', 'confirmed', 'packed', 'shipped', 'delivered', 'cancelled'];
const PAYMENT_OPTIONS = ['all', 'paid', 'pending', 'cod'];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [verifyingId, setVerifyingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [viewMode, setViewMode] = useState('tasks');
  const [taskFilter, setTaskFilter] = useState('all');

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancelOrderIntent, setCancelOrderIntent] = useState(null);
  const [codDeliverIntent, setCodDeliverIntent] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('orders')
      .select('*, profiles(full_name), order_items(*, products(name, image))')
      .order('created_at', { ascending: false });

    if (error) console.error("Error fetching orders:", error);
    if (data) setOrders(data);
    setLoading(false);
  };

  const updateStatus = async (id, status, currentOrder) => {
    if (status === 'cancelled' && currentOrder.payment_status === 'paid') {
      setCancelOrderIntent(currentOrder);
      return;
    }
    confirmStatusUpdate(id, status);
  };

  const confirmStatusUpdate = async (id, status, extraUpdates = {}) => {
    const updatePayload = { status, ...extraUpdates };
    const { error } = await supabase.from('orders').update(updatePayload).eq('id', id);
    if (error) {
      alert("Failed to update status: " + error.message);
      return;
    }

    fetchOrders();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      await supabase.functions.invoke('send-email', {
        body: { orderId: id, status_update: status },
        headers: { Authorization: `Bearer ${session?.access_token}` }
      });
    } catch (err) {
      console.error("Failed to send status email", err);
    }
  };

  const handleTaskAction = (order, nextStatus) => {
    const isCOD = order.shipping_address?.paymentMethod === 'cod';

    if (nextStatus === 'delivered' && isCOD) {
      setCodDeliverIntent(order);
      return;
    }

    confirmStatusUpdate(order.id, nextStatus);
  };

  const handleCancelOrder = (order) => {
    if (order.payment_status === 'paid') {
      setCancelOrderIntent(order);
    } else {
      if (window.confirm(`Cancel order #${order.id.split('-')[0].toUpperCase()}?`)) {
        confirmStatusUpdate(order.id, 'cancelled');
      }
    }
  };

  const handleManualVerify = async (orderId) => {
    setVerifyingId(orderId);
    try {
      const { data, error } = await supabase.functions.invoke('marx-verify', {
        body: { merchantReference: orderId }
      });

      if (error) throw error;

      if (data?.status === 'paid') {
        alert("Payment verified successfully! Order is now confirmed.");
        fetchOrders();
      } else {
        alert(`Verification result: ${data?.status || 'No change'}. Marx Status: ${data?.marx_status || 'Unknown'}`);
      }
    } catch (err) {
      alert("Verification failed: " + err.message);
    } finally {
      setVerifyingId(null);
    }
  };

  const handleDeleteOrder = async (id) => {
    if (window.confirm('Delete this order permanently? This will also remove associated payment records.')) {
      setLoading(true);
      try {
        await supabase.from('payment_logs').delete().eq('transaction_reference', id);
        await supabase.from('payment_transactions').delete().eq('order_id', id);
        await supabase.from('order_items').delete().eq('order_id', id);
        const { error } = await supabase.from('orders').delete().eq('id', id);

        if (error) throw error;
        fetchOrders();
      } catch (err) {
        alert("Delete failed: " + err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTogglePacked = async (order, itemId, isPacked) => {
    const currentSnapshot = order.order_snapshot || {};
    let packedItems = currentSnapshot.packed_items || [];

    if (isPacked) {
      packedItems = [...packedItems, itemId];
    } else {
      packedItems = packedItems.filter(id => id !== itemId);
    }

    const newSnapshot = { ...currentSnapshot, packed_items: packedItems };

    setSelectedOrder({ ...order, order_snapshot: newSnapshot });
    setOrders(orders.map(o => o.id === order.id ? { ...o, order_snapshot: newSnapshot } : o));

    await supabase.from('orders').update({ order_snapshot: newSnapshot }).eq('id', order.id);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setPaymentFilter('all');
    setSearchTerm('');
  };

  const hasActiveFilters = statusFilter !== 'all' || paymentFilter !== 'all' || searchTerm !== '';

  const filteredOrders = orders.filter(order => {
    const term = searchTerm.toLowerCase();
    const firstName = order.shipping_address?.firstName || '';
    const lastName = order.shipping_address?.lastName || '';
    const email = order.shipping_address?.email || '';
    const fullName = `${firstName} ${lastName}`.trim().toLowerCase();

    const matchesSearch = !term ||
      order.id.toLowerCase().includes(term) ||
      fullName.includes(term) ||
      email.toLowerCase().includes(term);

    const matchesStatus = statusFilter === 'all' || order.status?.toLowerCase() === statusFilter;

    const isCOD = order.shipping_address?.paymentMethod === 'cod';
    let matchesPayment = true;
    if (paymentFilter === 'paid') matchesPayment = order.payment_status === 'paid';
    else if (paymentFilter === 'pending') matchesPayment = order.payment_status !== 'paid' && !isCOD;
    else if (paymentFilter === 'cod') matchesPayment = isCOD;

    return matchesSearch && matchesStatus && matchesPayment;
  });

  const getStatusBadge = (status) => {
    const s = status.toLowerCase();
    switch (s) {
      case 'draft': return 'bg-gray-100 text-gray-500 border-gray-200';
      case 'pending': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'confirmed': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'packed': return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case 'shipped': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'delivered': return 'bg-green-50 text-green-700 border-green-200';
      case 'cancelled': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const statusCounts = orders.reduce((acc, o) => {
    const s = o.status?.toLowerCase() || 'unknown';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  // Pending tasks: orders that need admin action (pending, confirmed, packed, shipped)
  const pendingTasks = orders.filter(o => {
    const s = o.status?.toLowerCase();
    return s === 'pending' || s === 'confirmed' || s === 'packed' || s === 'shipped';
  });

  const taskGroups = {
    pending: pendingTasks.filter(o => o.status?.toLowerCase() === 'pending'),
    confirmed: pendingTasks.filter(o => o.status?.toLowerCase() === 'confirmed'),
    packed: pendingTasks.filter(o => o.status?.toLowerCase() === 'packed'),
    shipped: pendingTasks.filter(o => o.status?.toLowerCase() === 'shipped'),
  };

  // Filter tasks based on selected task filter
  const filteredTaskGroups = taskFilter === 'all' 
    ? taskGroups 
    : { [taskFilter]: taskGroups[taskFilter] || [] };

  const getNextAction = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return { label: 'Confirm Order', next: 'confirmed', icon: FiCheck, color: 'bg-blue-500' };
      case 'confirmed': return { label: 'Pack Order', next: 'packed', icon: FiBox, color: 'bg-indigo-500' };
      case 'packed': return { label: 'Mark Shipped', next: 'shipped', icon: FiTruck, color: 'bg-purple-500' };
      case 'shipped': return { label: 'Mark Delivered', next: 'delivered', icon: FiPackage, color: 'bg-green-500' };
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* View Mode Toggle */}
      <div className="flex items-center gap-2 bg-white rounded-2xl p-1.5 border border-gray-100 shadow-sm w-fit">
        <button
          onClick={() => setViewMode('tasks')}
          className={`px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${
            viewMode === 'tasks'
              ? 'bg-brand-darkSage text-white shadow-md'
              : 'text-gray-500 hover:text-brand-darkSage'
          }`}
        >
          <span className="flex items-center gap-2">
            <FiClock size={14} />
            Pending Tasks
            {pendingTasks.length > 0 && (
              <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] ${
                viewMode === 'tasks' ? 'bg-brand-gold text-white' : 'bg-brand-gold/20 text-brand-gold'
              }`}>
                {pendingTasks.length}
              </span>
            )}
          </span>
        </button>
        <button
          onClick={() => setViewMode('all')}
          className={`px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${
            viewMode === 'all'
              ? 'bg-brand-darkSage text-white shadow-md'
              : 'text-gray-500 hover:text-brand-darkSage'
          }`}
        >
          <span className="flex items-center gap-2">
            <FiFilter size={14} />
            All Orders
          </span>
        </button>
      </div>

      {/* Pending Tasks View */}
      {viewMode === 'tasks' && (
        <div className="space-y-6">
          {/* Refresh Button for Tasks */}
          <div className="flex justify-end">
            <button
              onClick={fetchOrders}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold uppercase tracking-widest text-gray-600 hover:border-brand-gold hover:text-brand-gold transition-all"
            >
              <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {pendingTasks.length === 0 ? (
            <div className="bg-white rounded-[2rem] border border-gray-100 shadow-xl p-16 text-center">
              <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiCheck} className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-2xl font-serif text-gray-800 mb-2">All Caught Up!</h3>
              <p className="text-gray-400 text-sm">No pending tasks right now. New orders will appear here.</p>
            </div>
          ) : (
            <>
              {/* Task Summary Cards - Clickable Filters */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <button
                  onClick={() => setTaskFilter('all')}
                  className={`p-4 rounded-2xl border-2 flex items-center gap-3 transition-all ${
                    taskFilter === 'all'
                      ? 'border-brand-darkSage bg-brand-darkSage/5 shadow-md'
                      : 'border-gray-100 bg-white hover:border-brand-gold/50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    taskFilter === 'all' ? 'bg-brand-darkSage text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    <SafeIcon icon={FiList} size={18} />
                  </div>
                  <div className="text-left">
                    <p className="text-xl font-bold">{pendingTasks.length}</p>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">All Tasks</p>
                  </div>
                </button>

                <button
                  onClick={() => setTaskFilter('pending')}
                  className={`p-4 rounded-2xl border-2 flex items-center gap-3 transition-all ${
                    taskFilter === 'pending'
                      ? 'border-yellow-400 bg-yellow-50 shadow-md'
                      : 'border-gray-100 bg-white hover:border-yellow-300'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    taskFilter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-yellow-100 text-yellow-600'
                  }`}>
                    <SafeIcon icon={FiCheck} size={18} />
                  </div>
                  <div className="text-left">
                    <p className="text-xl font-bold">{taskGroups.pending.length}</p>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">To Confirm</p>
                  </div>
                </button>

                <button
                  onClick={() => setTaskFilter('confirmed')}
                  className={`p-4 rounded-2xl border-2 flex items-center gap-3 transition-all ${
                    taskFilter === 'confirmed'
                      ? 'border-blue-400 bg-blue-50 shadow-md'
                      : 'border-gray-100 bg-white hover:border-blue-300'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    taskFilter === 'confirmed' ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-600'
                  }`}>
                    <SafeIcon icon={FiBox} size={18} />
                  </div>
                  <div className="text-left">
                    <p className="text-xl font-bold">{taskGroups.confirmed.length}</p>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">To Pack</p>
                  </div>
                </button>

                <button
                  onClick={() => setTaskFilter('packed')}
                  className={`p-4 rounded-2xl border-2 flex items-center gap-3 transition-all ${
                    taskFilter === 'packed'
                      ? 'border-indigo-400 bg-indigo-50 shadow-md'
                      : 'border-gray-100 bg-white hover:border-indigo-300'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    taskFilter === 'packed' ? 'bg-indigo-500 text-white' : 'bg-indigo-100 text-indigo-600'
                  }`}>
                    <SafeIcon icon={FiTruck} size={18} />
                  </div>
                  <div className="text-left">
                    <p className="text-xl font-bold">{taskGroups.packed.length}</p>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">To Ship</p>
                  </div>
                </button>

                <button
                  onClick={() => setTaskFilter('shipped')}
                  className={`p-4 rounded-2xl border-2 flex items-center gap-3 transition-all ${
                    taskFilter === 'shipped'
                      ? 'border-purple-400 bg-purple-50 shadow-md'
                      : 'border-gray-100 bg-white hover:border-purple-300'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    taskFilter === 'shipped' ? 'bg-purple-500 text-white' : 'bg-purple-100 text-purple-600'
                  }`}>
                    <SafeIcon icon={FiPackage} size={18} />
                  </div>
                  <div className="text-left">
                    <p className="text-xl font-bold">{taskGroups.shipped.length}</p>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">To Deliver</p>
                  </div>
                </button>
              </div>

              {/* Task Cards by Stage */}
              {Object.entries(filteredTaskGroups).map(([stage, stageOrders]) => {
                if (!stageOrders || stageOrders.length === 0) return null;
                const action = getNextAction(stage);
                const ActionIcon = action?.icon;

                return (
                  <div key={stage} className="bg-white rounded-[2rem] border border-gray-100 shadow-xl overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${action?.color} text-white`}>
                          <SafeIcon icon={ActionIcon} size={18} />
                        </div>
                        <div>
                          <h3 className="text-lg font-serif text-gray-800">{action?.label}</h3>
                          <p className="text-xs text-gray-400 font-medium uppercase tracking-widest">{stageOrders.length} order{stageOrders.length !== 1 ? 's' : ''} waiting</p>
                        </div>
                      </div>
                    </div>

                    <div className="divide-y divide-gray-50">
                      {stageOrders.map(order => {
                        const isCOD = order.shipping_address?.paymentMethod === 'cod';
                        const packedCount = order.order_snapshot?.packed_items?.length || 0;
                        const totalItems = order.order_items?.length || 0;

                        return (
                          <div key={order.id} className="p-5 hover:bg-gray-50/50 transition-colors">
                            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                              {/* Order Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                  <span className="text-xs font-black text-brand-gold">#{order.id.split('-')[0].toUpperCase()}</span>
                                  {/* Current Status Badge */}
                                  <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border-2 ${getStatusBadge(order.status)}`}>
                                    Current Status: {order.status}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                                    order.payment_status === 'paid' ? 'bg-green-50 text-green-600 border-green-100' :
                                    isCOD ? 'bg-orange-50 text-orange-600 border-orange-100' :
                                    'bg-yellow-50 text-yellow-600 border-yellow-100'
                                  }`}>
                                    {isCOD ? 'COD' : order.payment_status || 'Unpaid'}
                                  </span>
                                  <span className="text-[10px] text-gray-400">{new Date(order.created_at).toLocaleDateString()}</span>
                                </div>
                                <p className="text-sm font-bold text-gray-800">{order.shipping_address?.firstName} {order.shipping_address?.lastName}</p>
                                <p className="text-xs text-gray-500">{order.shipping_address?.email}</p>

                                {/* Packing progress for confirmed orders */}
                                {stage === 'confirmed' && totalItems > 0 && (
                                  <div className="mt-2 flex items-center gap-2">
                                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-[120px]">
                                      <div
                                        className="h-full bg-brand-gold rounded-full transition-all"
                                        style={{ width: `${(packedCount / totalItems) * 100}%` }}
                                      />
                                    </div>
                                    <span className="text-[10px] text-gray-400 font-medium">{packedCount}/{totalItems} packed</span>
                                  </div>
                                )}
                              </div>

                              {/* Items Preview */}
                              <div className="flex items-center gap-1.5">
                                {order.order_items?.slice(0, 3).map(item => (
                                  <div key={item.id} className="w-10 h-10 bg-gray-50 rounded-lg p-1 border border-gray-100">
                                    <img src={item.products?.image} alt="" className="w-full h-full object-contain" />
                                  </div>
                                ))}
                                {(order.order_items?.length || 0) > 3 && (
                                  <span className="text-[10px] text-gray-400 font-bold">+{order.order_items.length - 3}</span>
                                )}
                              </div>

                              {/* Amount */}
                              <div className="text-center lg:text-right">
                                <p className="text-sm font-black text-gray-800">Rs. {order.total_amount.toFixed(2)}</p>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setSelectedOrder(order)}
                                  className="p-2.5 rounded-xl border border-gray-200 text-gray-400 hover:text-brand-gold hover:border-brand-gold transition-all"
                                  title="View Details & Packing List"
                                >
                                  <FiEye size={16} />
                                </button>
                                <button
                                  onClick={() => handleCancelOrder(order)}
                                  className="p-2.5 rounded-xl border border-red-200 text-red-400 hover:text-red-600 hover:border-red-400 hover:bg-red-50 transition-all"
                                  title="Cancel Order"
                                >
                                  <FiXCircle size={16} />
                                </button>
                                <button
                                  onClick={() => handleTaskAction(order, action.next)}
                                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-[10px] font-bold uppercase tracking-widest transition-all shadow-md hover:shadow-lg ${action.color}`}
                                >
                                  <SafeIcon icon={ActionIcon} size={14} />
                                  {action.label}
                                  <FiChevronRight size={14} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* No tasks in selected filter */}
              {taskFilter !== 'all' && (!filteredTaskGroups[taskFilter] || filteredTaskGroups[taskFilter].length === 0) && (
                <div className="bg-white rounded-[2rem] border border-gray-100 shadow-xl p-12 text-center">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-serif text-gray-800 mb-2">No orders in this stage</h3>
                  <p className="text-gray-400 text-sm">All orders have moved past this stage.</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* All Orders View */}
      {viewMode === 'all' && (
        <div className="bg-white rounded-[2rem] border border-gray-100 shadow-xl overflow-hidden relative">
          {/* Header */}
          <div className="p-8 border-b border-gray-100">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-serif text-gray-800">Order Management</h2>
                  <button onClick={fetchOrders} className="p-2 text-brand-darkSage hover:text-brand-gold transition-colors bg-brand-sand/50 rounded-lg" title="Refresh Orders">
                    <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
                  </button>
                </div>
                <p className="text-xs text-gray-400 font-medium mt-1 uppercase tracking-widest">Manage fulfillment and track payments</p>
              </div>
              <div className="relative w-full lg:w-96">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by ID, Name, or Email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-gray-100 rounded-2xl focus:outline-none focus:border-brand-gold bg-gray-50/50"
                />
              </div>
            </div>

            {/* Filter Bar */}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-gray-400 font-bold uppercase tracking-widest">
                <FiFilter size={13} /> Filters
              </div>

              <div className="flex flex-wrap gap-1.5">
                {['all', ...ADMIN_STATUS_OPTIONS].map(status => {
                  const count = status === 'all' ? orders.length : (statusCounts[status] || 0);
                  if (status !== 'all' && count === 0) return null;
                  return (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all ${
                        statusFilter === status
                          ? 'bg-brand-darkSage text-white border-brand-darkSage shadow-md'
                          : 'bg-white text-gray-500 border-gray-200 hover:border-brand-gold hover:text-brand-gold'
                      }`}
                    >
                      {status}
                      <span className={`ml-1.5 ${statusFilter === status ? 'text-white/60' : 'text-gray-300'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="w-px h-6 bg-gray-200 mx-1 hidden sm:block" />

              <div className="flex flex-wrap gap-1.5">
                {PAYMENT_OPTIONS.map(payment => (
                  <button
                    key={payment}
                    onClick={() => setPaymentFilter(payment)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all ${
                      paymentFilter === payment
                        ? 'bg-brand-gold text-white border-brand-gold shadow-md'
                        : 'bg-white text-gray-500 border-gray-200 hover:border-brand-gold hover:text-brand-gold'
                    }`}
                  >
                    {payment === 'all' ? 'All Payments' : payment === 'cod' ? 'COD' : `Payment: ${payment}`}
                  </button>
                ))}
              </div>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-red-400 border border-red-200 hover:bg-red-50 transition-all"
                >
                  <FiX size={11} /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/80 text-gray-400 text-[10px] font-black uppercase tracking-[0.2em]">
                  <th className="p-6">Order</th>
                  <th className="p-6">Customer</th>
                  <th className="p-6 text-center">Amount</th>
                  <th className="p-6 text-center">Payment</th>
                  <th className="p-6 text-center">Fulfillment</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-16 text-center">
                      <p className="text-gray-400 text-sm">No orders match your filters.</p>
                    </td>
                  </tr>
                ) : filteredOrders.map(order => {
                  const isCOD = order.shipping_address?.paymentMethod === 'cod';

                  return (
                    <tr key={order.id} className="hover:bg-gray-50/30 transition-colors">
                      <td className="p-6">
                        <p className="text-xs font-black text-brand-gold mb-1">#{order.id.split('-')[0].toUpperCase()}</p>
                        <p className="text-[10px] text-gray-400">{new Date(order.created_at).toLocaleDateString()}</p>
                      </td>
                      <td className="p-6">
                        <p className="text-sm font-bold text-gray-800">{order.shipping_address?.firstName} {order.shipping_address?.lastName}</p>
                        <p className="text-xs text-gray-500 font-mono line-clamp-1">{order.shipping_address?.email}</p>
                      </td>
                      <td className="p-6 text-center">
                        <p className="text-sm font-black text-gray-800">Rs. {order.total_amount.toFixed(2)}</p>
                      </td>
                      <td className="p-6 text-center">
                        <div className="flex flex-col items-center gap-1.5">
                          <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border-2 ${
                            order.payment_status === 'paid' ? 'bg-green-50 text-green-600 border-green-100' :
                            isCOD ? 'bg-orange-50 text-orange-600 border-orange-100' :
                            'bg-yellow-50 text-yellow-600 border-yellow-100'
                          }`}>
                            {isCOD ? 'COD' : order.payment_status || 'Unpaid'}
                          </span>
                          {!isCOD && order.payment_status !== 'paid' && order.status !== 'draft' && order.status !== 'pending' && (
                            <button
                              onClick={() => handleManualVerify(order.id)}
                              disabled={verifyingId === order.id}
                              className="text-[9px] font-black text-brand-gold hover:text-brand-darkSage flex items-center gap-1"
                            >
                              <FiRefreshCw size={10} className={verifyingId === order.id ? 'animate-spin' : ''} /> Sync
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="p-6 text-center">
                        <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border-2 ${getStatusBadge(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="p-6 text-right space-x-2 whitespace-nowrap">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="text-gray-400 hover:text-brand-gold p-2 transition-colors mr-1"
                          title="View Order Details & Packing List"
                        >
                          <FiEye size={16} />
                        </button>
                        <select
                          value={order.status.toLowerCase()}
                          onChange={(e) => updateStatus(order.id, e.target.value, order)}
                          className="text-[10px] font-black uppercase tracking-widest border-2 border-gray-100 rounded-xl px-2 py-1.5 bg-white outline-none"
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="packed">Packed</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        <button
                          onClick={() => handleDeleteOrder(order.id)}
                          className="text-gray-300 hover:text-red-500 p-2"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {cancelOrderIntent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiAlertTriangle} className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-xl font-serif text-center text-gray-800 mb-4">
              {cancelOrderIntent.payment_status === 'paid' ? 'Manual Refund Required' : 'Cancel Order'}
            </h3>
            <p className="text-gray-600 text-sm text-center mb-8 leading-relaxed">
              {cancelOrderIntent.payment_status === 'paid' ? (
                <>
                  This order is marked as <strong className="text-brand-darkSage">Paid</strong>. Since you are cancelling it, please ensure you process the refund manually through the Marx Gateway dashboard.
                  <br/><br/>
                  Have you initiated the refund or noted this down?
                </>
              ) : (
                <>
                  Are you sure you want to cancel order <strong>#{cancelOrderIntent.id.split('-')[0].toUpperCase()}</strong>?
                  <br/><br/>
                  The customer will be notified of the cancellation.
                </>
              )}
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setCancelOrderIntent(null)}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold text-xs uppercase tracking-widest hover:bg-gray-50 transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={() => {
                  confirmStatusUpdate(cancelOrderIntent.id, 'cancelled');
                  setCancelOrderIntent(null);
                }}
                className="flex-1 px-4 py-3 rounded-xl bg-red-500 text-white font-bold text-xs uppercase tracking-widest hover:bg-red-600 transition-colors shadow-md shadow-red-500/20"
              >
                Yes, Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* COD Delivery Payment Confirmation Modal */}
      {codDeliverIntent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiDollarSign} className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-serif text-center text-gray-800 mb-2">COD Payment Status</h3>
            <p className="text-gray-500 text-xs text-center mb-6">
              Order #{codDeliverIntent.id.split('-')[0].toUpperCase()} • Rs. {codDeliverIntent.total_amount.toFixed(2)}
            </p>
            <p className="text-gray-600 text-sm text-center mb-8 leading-relaxed">
              This is a <strong className="text-orange-600">Cash on Delivery</strong> order. Has the customer paid the delivery rider?
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  confirmStatusUpdate(codDeliverIntent.id, 'delivered', { payment_status: 'paid', paid_at: new Date().toISOString() });
                  setCodDeliverIntent(null);
                }}
                className="w-full px-4 py-3.5 rounded-xl bg-green-500 text-white font-bold text-xs uppercase tracking-widest hover:bg-green-600 transition-colors shadow-md shadow-green-500/20 flex items-center justify-center gap-2"
              >
                <FiCheck size={16} />
                Yes, Payment Received
              </button>
              <button
                onClick={() => {
                  confirmStatusUpdate(codDeliverIntent.id, 'delivered');
                  setCodDeliverIntent(null);
                }}
                className="w-full px-4 py-3.5 rounded-xl bg-orange-500 text-white font-bold text-xs uppercase tracking-widest hover:bg-orange-600 transition-colors shadow-md shadow-orange-500/20 flex items-center justify-center gap-2"
              >
                <FiClock size={16} />
                No, Payment Pending
              </button>
              <button
                onClick={() => setCodDeliverIntent(null)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-500 font-bold text-xs uppercase tracking-widest hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Details & Packing List Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl relative max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button
              onClick={() => setSelectedOrder(null)}
              className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 p-2 rounded-full"
            >
              <FiX size={20} />
            </button>

            <h3 className="text-2xl font-serif text-gray-800 mb-6 border-b border-gray-100 pb-4">Order Details</h3>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Order ID</p>
                <p className="font-mono text-sm font-bold text-brand-darkSage">#{selectedOrder.id.split('-')[0].toUpperCase()}</p>

                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mt-4 mb-1">Status</p>
                <span className={`inline-block px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border-2 ${getStatusBadge(selectedOrder.status)}`}>
                  {selectedOrder.status}
                </span>

                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mt-4 mb-1">Total</p>
                <p className="text-sm font-bold text-gray-800">Rs. {selectedOrder.total_amount.toFixed(2)}</p>
              </div>

              <div className="bg-brand-sand/30 p-4 rounded-2xl border border-brand-darkSage/10">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Customer Info</p>
                <p className="text-sm font-bold text-gray-800">{selectedOrder.shipping_address?.firstName} {selectedOrder.shipping_address?.lastName}</p>
                <p className="text-xs text-gray-600 mt-1">{selectedOrder.shipping_address?.email}</p>
                <p className="text-xs text-gray-600 mt-1">{selectedOrder.shipping_address?.countryCode} {selectedOrder.shipping_address?.phone}</p>
                <div className="mt-3 pt-3 border-t border-brand-darkSage/10">
                  <p className="text-xs text-gray-700 leading-relaxed">
                    {selectedOrder.shipping_address?.address}, <br/>
                    {selectedOrder.shipping_address?.city}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-bold uppercase tracking-widest text-gray-800 flex items-center gap-2">
                <SafeIcon icon={FiCheckSquare} className="text-brand-gold" /> Packing Checklist
              </h4>
              <span className="text-xs text-gray-500">
                {selectedOrder.order_snapshot?.packed_items?.length || 0} / {selectedOrder.order_items?.length || 0} Packed
              </span>
            </div>

            <div className="space-y-3">
              {selectedOrder.order_items?.map(item => {
                const isPacked = selectedOrder.order_snapshot?.packed_items?.includes(item.id);
                return (
                  <label key={item.id} className={`flex items-center gap-4 p-3 rounded-xl border transition-colors cursor-pointer ${isPacked ? 'border-brand-gold/50 bg-brand-gold/5' : 'border-gray-100 hover:bg-gray-50'}`}>
                    <input
                      type="checkbox"
                      checked={!!isPacked}
                      onChange={(e) => handleTogglePacked(selectedOrder, item.id, e.target.checked)}
                      className="w-5 h-5 accent-brand-gold cursor-pointer rounded"
                    />
                    <img src={item.products?.image} alt={item.products?.name} className="w-12 h-12 object-contain bg-white rounded-lg border border-gray-100" />
                    <div className="flex-1">
                      <p className={`text-sm font-bold ${isPacked ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                        {item.products?.name}
                      </p>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">Qty: {item.quantity}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;